import { z } from 'zod';

// Citation schemas matching Anthropic's Citations API format.
// Factory returns fresh instances: a Zod instance reused within one schema
// tree converts to a non-top-level $ref, which OpenAI/Anthropic structured
// outputs reject.
export const makeCitationSchema = () =>
  z.union([
    z.object({
      type: z.literal('char_location'),
      cited_text: z.string(),
      document_index: z.number(),
      document_title: z.string().optional(),
      start_char_index: z.number(),
      end_char_index: z.number(),
      artifact_id: z.string().optional(), // ID from external_patient_artifacts table
    }),
    z.object({
      type: z.literal('page_location'),
      cited_text: z.string(),
      document_index: z.number(),
      document_title: z.string().optional(),
      start_page_number: z.number(),
      end_page_number: z.number(),
      artifact_id: z.string().optional(), // ID from external_patient_artifacts table
    }),
    z.object({
      type: z.literal('content_block_location'),
      cited_text: z.string(),
      document_index: z.number(),
      document_title: z.string().optional(),
      start_block_index: z.number(),
      end_block_index: z.number(),
      artifact_id: z.string().optional(), // ID from external_patient_artifacts table
    }),
  ]);

export const citationSchema = makeCitationSchema();

export const differentialDiagnosisSchema = z.object({
  candidate_diagnosis: z.string(),
  supporting_evidence: z.array(z.string()),
  signal_strength: z.enum(['strong', 'moderate', 'weak']),
  clinical_urgency: z.enum(['strong', 'moderate', 'weak']),
  current_differential_gap: z.string(),
  suggested_next_steps: z.string(),
  key_uncertainty: z.string(),
  gates_passed: z.array(z.string()),
  citations: z.array(citationSchema).optional(), // Overall citations for the diagnosis, separate from those tied to specific indicators
});

export const differentialDiagnosesSchema = z.array(differentialDiagnosisSchema);

// Lenient variants for rendering stored outputs: fields added after an output
// was generated fall back to defaults instead of dropping the whole output.
// Generation/extraction must keep using the strict schemas above.
export const differentialDiagnosisDisplaySchema = z.object({
  candidate_diagnosis: z.string(),
  supporting_evidence: z.array(z.string()).catch([]),
  signal_strength: z.enum(['strong', 'moderate', 'weak']).catch('moderate'),
  clinical_urgency: z.enum(['strong', 'moderate', 'weak']).catch('moderate'),
  current_differential_gap: z.string().catch(''),
  suggested_next_steps: z.string().catch(''),
  key_uncertainty: z.string().catch(''),
  gates_passed: z.array(z.string()).catch([]),
  citations: z.array(makeCitationSchema()).optional().catch(undefined),
});

export const differentialDiagnosesDisplaySchema = z.array(
  differentialDiagnosisDisplaySchema,
);

export type Citation = z.infer<typeof citationSchema>;
export type DifferentialDiagnosisOutputSchema = z.infer<
  typeof differentialDiagnosisSchema
>;

export type DifferentialDiagnosesOutputSchema = z.infer<
  typeof differentialDiagnosesSchema
>;
